import React, { useState, useEffect } from 'react';
import posthog from 'posthog-js';
import { EventCard as EventCardType } from '../types';
import { processMarketQuestions } from '../utils/marketQuestionProcessor';
import { calculateTimeRemaining, formatTimeRemaining, TimeRemaining } from '../utils/timeUtils';
import './EventCard.css';
import { extractSurnamesFromTitle, extractCricketSidesFromTitle } from '../utils/headToHead';

interface EventCardProps {
  event: EventCardType;
  onClick?: (event: EventCardType) => void;
  isSelected?: boolean;
  onOpenPurchaseModal?: (eventTitle: string, marketQuestion: string, outcome: 'yes' | 'no', outcomePrice: number, marketId: string, endDate?: string, marketIcon?: string) => void;
  isNew?: boolean;
  isTrending?: boolean;
  parlayLegs?: Array<{
    eventTitle: string;
    marketQuestion: string;
    outcome: 'yes' | 'no';
    outcomePrice: number;
    decimalOdds: number;
    endDate?: string;
    marketIcon?: string;
    marketId: string;
    sportsTeamData?: any;
  }>;
}

const EventCard: React.FC<EventCardProps> = ({ event, onClick, isSelected, onOpenPurchaseModal, isNew = false, isTrending = false, parlayLegs = [] }) => {
  const [processedQuestions, setProcessedQuestions] = useState<{ [key: string]: string }>({});
  const [isProcessing, setIsProcessing] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>({ days: 0, hours: 0, minutes: 0, seconds: 0, isExpired: true });
  const [selectedMarketForPurchase, setSelectedMarketForPurchase] = useState<any | null>(null);
  const [selectedOutcomeForPurchase, setSelectedOutcomeForPurchase] = useState<'yes' | 'no' | null>(null);
  const [showVolumeTooltip, setShowVolumeTooltip] = useState(false);
  const [showTrendingTooltip, setShowTrendingTooltip] = useState(false);
  const [hoveredOutcome, setHoveredOutcome] = useState<{ marketId: string; outcome: 'yes' | 'no' } | null>(null);

  const formatVolume = (volume: number) => {
    if (volume >= 1000000) {
      return `$${Math.round(volume / 1000000)}m`;
    }
    if (volume >= 1000) {
      return `$${Math.round(volume / 1000)}k`;
    }
    return `$${volume}`;
  };

  const formatVolume24hr = (volume: number) => {
    if (volume >= 1000000) {
      return `$${(volume / 1000000).toFixed(1)}m`;
    }
    if (volume >= 1000) {
      return `$${(volume / 1000).toFixed(1)}k`;
    }
    return `$${Math.round(volume)}`;
  };

  // Calculate estimated multiplier if this market was added to parlay
  const calculateEstimatedMultiplier = (market: any, outcome: 'yes' | 'no'): string => {
    // Calculate existing multiplier from current parlay legs
    const existingMultiplier = parlayLegs.reduce((acc, leg) => acc * leg.decimalOdds, 1.0);

    // Get the outcome price for the new market
    const outcomePrice = outcome === 'yes' ? market.outcome_prices[0] : market.outcome_prices[1];

    // Calculate new odds (decimal odds = 1 / probability)
    const newOdds = outcomePrice > 0 ? (1 / outcomePrice) : 1.0;

    // Calculate total multiplier
    const totalMultiplier = existingMultiplier * newOdds;

    // Return formatted multiplier (e.g., "2.75")
    return totalMultiplier.toFixed(2);
  };

  // Display helper: avoid rounding extremes
  // - For prices >= 0.99 and < 1.0, show true odds without rounding up
  //   using two decimals via truncation (e.g., 0.9996 -> 99.96, not 100.0)
  // - For prices > 0 and < 0.01, show two decimals without rounding down
  //   using truncation; if result is 0.00, display as 0.01 to avoid 0.00 visual
  // - Otherwise, keep existing one-decimal rounding
  const formatPrice = (price: number) => {
    const p = price || 0;
    if (p > 0 && p < 0.01) {
      const truncatedTwoDecimals = Math.floor(p * 10000) / 100;
      const val = truncatedTwoDecimals <= 0 ? 0.01 : truncatedTwoDecimals;
      return val.toFixed(2);
    }
    if (p >= 0.99 && p < 1) {
      const truncatedTwoDecimals = Math.floor(p * 10000) / 100; // truncate to 2 decimals
      return truncatedTwoDecimals.toFixed(2);
    }
    return (p * 100).toFixed(1);
  };

  // Numeric percent for comparisons (aligns with display logic)
  const displayPercentNumber = (price: number) => {
    const p = price || 0;
    if (p > 0 && p < 0.01) {
      return Math.max(0.01, Math.floor(p * 10000) / 100);
    }
    if (p >= 0.99 && p < 1) {
      return Math.floor(p * 10000) / 100; // two-decimal truncated
    }
    return Number((p * 100).toFixed(1));
  };

  // Gauge should never jump to 100% unless truly 1.0
  const gaugePercent = (price: number) => {
    const p = price || 0;
    if (p >= 0.99 && p < 1) {
      return Math.floor(p * 100); // truncate to integer to avoid 100
    }
    return Math.round(p * 100);
  };

  const handleYesNoClick = (e: React.MouseEvent, market: any, outcome: 'yes' | 'no') => {
    e.preventDefault();
    e.stopPropagation();

    // Track market entry from event card
    try {
      posthog.capture('engagement:market_entry', {
        source: 'event_card',
        event_title: event.title,
        category: (event as any).category || null,
        sub_category: (event as any).sub_category || null,
        market_id: (market.market_id || (market as any).id || null),
        outcome,
        is_authenticated: false,
      });
    } catch {}

    if (isSingleMarket && onOpenPurchaseModal) {
      // For single-market events, open PurchaseModal directly
      const outcomePrice = outcome === 'yes' ? market.outcome_prices[0] : market.outcome_prices[1];
      const marketQuestion = isProcessing ? market.question : (processedQuestions[market.question] || market.question);

      // Transform {p1, p2} to {yes, no} for PurchaseModal
      const labels = tennisLabels || cricketLabels;
      // Up/Down override when outcomes are Up/Down OR title/question contains "Up or Down"
      const marketOutcomes = (market.outcomes || []).map((o: any) => String(o).toLowerCase());
      const titleOrQ = `${event.title} ${marketQuestion}`;
      const isUpDown = (marketOutcomes.length >= 2 && marketOutcomes[0] === 'up' && marketOutcomes[1] === 'down')
        || /\bup\s*or\s*down\b/i.test(titleOrQ);
      const propsLabels = isUpDown
        ? { yes: 'Up', no: 'Down' }
        : (labels ? { yes: labels.p1, no: labels.p2 } : undefined);

      (onOpenPurchaseModal as any)(
        event.title,
        marketQuestion,
        outcome,
        outcomePrice,
        (market.market_id || (market as any).id),
        (market.end_date || (market as any).ends_at),
        market.icon || event.image,
        propsLabels
      );
    } else {
      // For multi-market events, set the selected market and outcome for the purchase interface
      setSelectedMarketForPurchase(market);
      setSelectedOutcomeForPurchase(outcome);
    }
  };

  const handlePurchaseClick = (e: React.MouseEvent, outcome: 'yes' | 'no') => {
    e.preventDefault();
    e.stopPropagation();

    if (!onOpenPurchaseModal || !selectedMarketForPurchase) return;

    const outcomePrice = outcome === 'yes' ? selectedMarketForPurchase.outcome_prices[0] : selectedMarketForPurchase.outcome_prices[1];
    const marketQuestion = isProcessing ? selectedMarketForPurchase.question : (processedQuestions[selectedMarketForPurchase.question] || selectedMarketForPurchase.question);

    // Transform {p1, p2} to {yes, no} for PurchaseModal
    const labels = tennisLabels || cricketLabels;
    const marketOutcomes = (selectedMarketForPurchase.outcomes || []).map((o: any) => String(o).toLowerCase());
    const titleOrQ = `${event.title} ${marketQuestion}`;
    const isUpDown = (marketOutcomes.length >= 2 && marketOutcomes[0] === 'up' && marketOutcomes[1] === 'down')
      || /\bup\s*or\s*down\b/i.test(titleOrQ);
    const propsLabels = isUpDown
      ? { yes: 'Up', no: 'Down' }
      : (labels ? { yes: labels.p1, no: labels.p2 } : undefined);

    // Emit selection capture for direct selection from event card purchase UI
    try {
      posthog.capture('engagement:market_selected', {
        source: 'event_card',
        market_id: (selectedMarketForPurchase.market_id || (selectedMarketForPurchase as any).id),
        market_question: marketQuestion,
        outcome,
        outcome_price: outcomePrice,
        event_title: event.title,
        category: (event as any).category || null,
        sub_category: (event as any).sub_category || null,
      });
    } catch {}

    (onOpenPurchaseModal as any)(
      event.title,
      marketQuestion,
      outcome,
      outcomePrice,
      (selectedMarketForPurchase.market_id || (selectedMarketForPurchase as any).id),
      (selectedMarketForPurchase.end_date || (selectedMarketForPurchase as any).ends_at),
      selectedMarketForPurchase.icon || event.image,
      propsLabels
    );
    
    // Reset the purchase interface after opening modal
    setSelectedMarketForPurchase(null);
    setSelectedOutcomeForPurchase(null);
  };

  const calculatePayout = (baseAmount: number, price: number) => {
    if (price === 0) return baseAmount;
    return Math.round(baseAmount / price);
  };

  useEffect(() => {
    const processQuestions = async () => {
      try {
        const filteredMarkets = event.top_markets.filter(market => {
          const yesPrice = market.outcome_prices[0] || 0;
          const noPrice = market.outcome_prices[1] || 0;

          // Filter out resolved/extreme markets (≤0.1% or ≥99.9%)
          return !(yesPrice <= 0.001 || yesPrice >= 0.999 ||
                   noPrice <= 0.001 || noPrice >= 0.999);
        });

        if (filteredMarkets.length > 0) {
          const questions = filteredMarkets.map(market => market.question);
          const processed = await processMarketQuestions({
            eventTitle: event.title,
            marketQuestions: questions
          });

          // Create mapping from original question to processed question
          // Skip markets that were marked as outliers (None/null values)
          const questionMap: { [key: string]: string } = {};
          questions.forEach((original, index) => {
            const processedResult = processed[index];
            if (processedResult && processedResult !== null) {
              questionMap[original] = processedResult;
            }
            // If processedResult is null/None, don't add to map (effectively filters it out)
          });
          
          setProcessedQuestions(questionMap);
        }
      } catch (error) {
        // Fallback to empty mapping - will show original questions
        setProcessedQuestions({});
      } finally {
        setIsProcessing(false);
      }
    };

    processQuestions();
  }, [event]);

  // Timer effect
  useEffect(() => {
    const updateTimer = () => {
      setTimeRemaining(calculateTimeRemaining(event.ends_at));
    };

    // Initial calculation
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [event.ends_at]);

  // Determine active (unresolved) markets and whether this is a single-market card
  // Selection rules:
  // - Exclude resolved markets (exact 0% or 100%)
  // - Prefer higher Yes price
  // - Ensure the second market displayed has a strictly lower displayed % than the first
  const sortedCandidates = event.top_markets
    .filter(market => {
      const yesPrice = market.outcome_prices[0] || 0;
      const noPrice = market.outcome_prices[1] || 0;
      // Filter out resolved/extreme markets (≤0.1% or ≥99.9%)
      return !(yesPrice <= 0.001 || yesPrice >= 0.999 ||
               noPrice <= 0.001 || noPrice >= 0.999);
    })
    .filter(market => isProcessing || processedQuestions[market.question] !== undefined || Object.keys(processedQuestions).length === 0)
    .sort((a, b) => (b.outcome_prices[0] || 0) - (a.outcome_prices[0] || 0));

  const pickTopTwoStrict = () => {
    if (sortedCandidates.length === 0) return [] as typeof sortedCandidates;
    const first = sortedCandidates[0];
    const firstDisplayed = displayPercentNumber(first.outcome_prices[0] || 0);
    const selection = [first];
    for (let i = 1; i < sortedCandidates.length; i++) {
      const candidate = sortedCandidates[i];
      const candDisplayed = displayPercentNumber(candidate.outcome_prices[0] || 0);
      if (candDisplayed < firstDisplayed) {
        selection.push(candidate);
        break;
      }
    }
    return selection;
  };

  const activeMarkets = pickTopTwoStrict();

  const isSingleMarket = activeMarkets.length === 1;
  // Tennis label logic
  const isTennis = (event as any).sub_category === 'Tennis' || /Tennis/i.test((event as any).category || '');
  const isCricket = (event as any).sub_category === 'Cricket' || /Cricket/i.test((event as any).category || '');
  const tennisLabels = isTennis ? extractSurnamesFromTitle(event.title) : null;
  const cricketLabels = !tennisLabels && isCricket ? extractCricketSidesFromTitle(event.title) : null;
  // Determine Up/Down override for single-market CTA labels
  const active = activeMarkets[0];
  const activeOutcomesLower = (active?.outcomes || []).map((o: any) => String(o).toLowerCase());
  const activeQText = active ? (isProcessing ? active.question : (processedQuestions[active.question] || active.question)) : '';
  const isUpDownSingle = !!active && (
    (activeOutcomesLower.length >= 2 && activeOutcomesLower[0] === 'up' && activeOutcomesLower[1] === 'down') ||
    /\bup\s*or\s*down\b/i.test(`${event.title} ${activeQText}`)
  );
  const baseYesLabel = tennisLabels ? tennisLabels.p1 : (cricketLabels ? cricketLabels.p1 : 'Yes');
  const baseNoLabel = tennisLabels ? tennisLabels.p2 : (cricketLabels ? cricketLabels.p2 : 'No');
  const yesLabel = isUpDownSingle ? 'Up' : baseYesLabel;
  const noLabel = isUpDownSingle ? 'Down' : baseNoLabel;

  const renderGauge = (percentageNumber: number) => {
    // Clamp to [0,100]
    const p = Math.max(0, Math.min(100, percentageNumber));
    // Semi-circle gauge math
    const radius = 32;
    const circumference = Math.PI * radius; // half circle length
    const progress = (p / 100) * circumference;
    // Color thresholds
    let colorClass = 'gauge-red';
    if (p >= 60) colorClass = 'gauge-green';
    else if (p >= 30) colorClass = 'gauge-yellow';

    return (
      <div className={`chance-gauge ${colorClass}`} aria-label={`Chance ${p}%`}>
        <svg viewBox="0 0 88 56" className="gauge-svg">
          <path className="gauge-bg" d="M12 44 A32 32 0 0 1 76 44" />
          <path
            className="gauge-fg"
            d="M12 44 A32 32 0 0 1 76 44"
            style={{ strokeDasharray: `${progress} ${circumference}` }}
          />
        </svg>
        <div className="gauge-content">
          <div className="gauge-number">{p}%</div>
          <div className="gauge-label">chance</div>
        </div>
      </div>
    );
  };

  return (
    <div className={`event-card ${isSelected ? 'selected' : ''}`} onClick={() => onClick?.(event)}>
      <div className="event-header">
        {event.image && (
          <img src={event.image} alt={event.title} className="event-image" />
        )}
        <h3 className="event-title">{event.title}</h3>
        {isSingleMarket && (
          <div className="header-gauge-wrapper">
            {renderGauge(gaugePercent(activeMarkets[0].outcome_prices[0] || 0))}
          </div>
        )}
      </div>

      {!isSingleMarket && !selectedMarketForPurchase && (
        <div className="markets-container">
          {activeMarkets.map((market, index) => (
            <div key={index} className="market-row">
              <div className="market-question">
                {isProcessing ? market.question : (processedQuestions[market.question] || market.question)}
              </div>
              <div className="market-price">
                {formatPrice(market.outcome_prices[0] || 0)}%
              </div>
              <div className="outcome-buttons">
                {market.outcomes.map((outcome, outcomeIndex) => {
                  const outcomesLower = (market.outcomes || []).map((o: any) => String(o).toLowerCase());
                  const isUpDown = (outcomesLower.length >= 2 && outcomesLower[0] === 'up' && outcomesLower[1] === 'down')
                    || /\bup\s*or\s*down\b/i.test(`${event.title} ${isProcessing ? market.question : (processedQuestions[market.question] || market.question)}`);
                  const isYes = isUpDown ? (outcomeIndex === 0) : (String(outcome).toLowerCase() === 'yes');
                  const label = isUpDown
                    ? (outcomeIndex === 0 ? 'Up' : 'Down')
                    : (tennisLabels ? (isYes ? yesLabel : noLabel) : String(outcome));
                  const currentOutcome = isYes ? 'yes' : 'no';
                  const marketId = market.market_id || `${event.title}::${market.question}`;
                  const isHovered = hoveredOutcome?.marketId === marketId && hoveredOutcome?.outcome === currentOutcome;
                  const showTooltip = isHovered && parlayLegs.length >= 1;

                  return (
                    <button
                      key={outcomeIndex}
                      className={`outcome-button ${isYes ? 'yes-button' : 'no-button'}`}
                      onClick={(e) => handleYesNoClick(e, market, isYes ? 'yes' : 'no')}
                      onMouseEnter={() => {
                        setHoveredOutcome({ marketId, outcome: currentOutcome });
                      }}
                      onMouseLeave={() => setHoveredOutcome(null)}
                    >
                      {label}
                      {showTooltip && (
                        <span className="parlay-estimate-tooltip">
                          Estimated parlay odds if added: {calculateEstimatedMultiplier(market, currentOutcome)}x
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {!isSingleMarket && selectedMarketForPurchase && (
        <div className="purchase-interface">
          <div className="purchase-header">
            <div className="purchase-question">
              {isProcessing ? selectedMarketForPurchase.question : (processedQuestions[selectedMarketForPurchase.question] || selectedMarketForPurchase.question)}
            </div>
            <div className="purchase-percentage">
              {formatPrice(selectedMarketForPurchase.outcome_prices[0] || 0)}%
            </div>
            <button 
              className="back-button-small"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedMarketForPurchase(null);
                setSelectedOutcomeForPurchase(null);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M7.646 4.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1-.708.708L8 5.707l-5.646 5.647a.5.5 0 0 1-.708-.708z"/>
              </svg>
            </button>
          </div>
          <div className="purchase-buttons">
            <button 
              className="purchase-yes-btn"
              onClick={(e) => handlePurchaseClick(e, 'yes')}
            >
              {yesLabel}
            </button>
            <button 
              className="purchase-no-btn"
              onClick={(e) => handlePurchaseClick(e, 'no')}
            >
              {noLabel}
            </button>
          </div>
        </div>
      )}

      {isSingleMarket && (
        <div className="single-market-cta">
          <button
            className="cta-button cta-yes"
            onClick={(e) => handleYesNoClick(e, activeMarkets[0], 'yes')}
            onMouseEnter={() => {
              const marketId = activeMarkets[0].market_id || `${event.title}::${activeMarkets[0].question}`;
              setHoveredOutcome({ marketId, outcome: 'yes' });
            }}
            onMouseLeave={() => setHoveredOutcome(null)}
          >
            {yesLabel}
            {hoveredOutcome?.marketId === (activeMarkets[0].market_id || `${event.title}::${activeMarkets[0].question}`) && hoveredOutcome?.outcome === 'yes' && parlayLegs.length >= 1 && (
              <span className="parlay-estimate-tooltip">
                Estimated parlay odds if added: {calculateEstimatedMultiplier(activeMarkets[0], 'yes')}x
              </span>
            )}
          </button>
          <button
            className="cta-button cta-no"
            onClick={(e) => handleYesNoClick(e, activeMarkets[0], 'no')}
            onMouseEnter={() => {
              const marketId = activeMarkets[0].market_id || `${event.title}::${activeMarkets[0].question}`;
              setHoveredOutcome({ marketId, outcome: 'no' });
            }}
            onMouseLeave={() => setHoveredOutcome(null)}
          >
            {noLabel}
            {hoveredOutcome?.marketId === (activeMarkets[0].market_id || `${event.title}::${activeMarkets[0].question}`) && hoveredOutcome?.outcome === 'no' && parlayLegs.length >= 1 && (
              <span className="parlay-estimate-tooltip">
                Estimated parlay odds if added: {calculateEstimatedMultiplier(activeMarkets[0], 'no')}x
              </span>
            )}
          </button>
        </div>
      )}

      <div className="event-footer">
        <span
          className="volume"
          onMouseEnter={() => setShowVolumeTooltip(true)}
          onMouseLeave={() => setShowVolumeTooltip(false)}
        >
          {formatVolume(event.volume)}
          {showVolumeTooltip && (
            <span className="volume-tooltip">
              Amount traded on this market on Polymarket
            </span>
          )}
        </span>
        <div className="action-icons">
        </div>
        {isTrending ? (
          <span
            className="trending-badge"
            onMouseEnter={() => setShowTrendingTooltip(true)}
            onMouseLeave={() => setShowTrendingTooltip(false)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M0 0h1v15h15v1H0zm14.817 3.113a.5.5 0 0 1 .07.704l-4.5 5.5a.5.5 0 0 1-.74.037L7.06 6.767l-3.656 5.027a.5.5 0 0 1-.808-.588l4-5.5a.5.5 0 0 1 .758-.06l2.609 2.61 4.15-5.073a.5.5 0 0 1 .704-.07"/>
            </svg>
            {formatVolume24hr(event.volume24hr || 0)}
            {showTrendingTooltip && (
              <span className="trending-tooltip">
                24 hour trading volume
              </span>
            )}
          </span>
        ) : isNew ? (
          <span className="new-badge">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path d="M7.657 6.247c.11-.33.576-.33.686 0l.645 1.937a2.89 2.89 0 0 0 1.829 1.828l1.936.645c.33.11.33.576 0 .686l-1.937.645a2.89 2.89 0 0 0-1.828 1.829l-.645 1.936a.361.361 0 0 1-.686 0l-.645-1.937a2.89 2.89 0 0 0-1.828-1.828l-1.937-.645a.361.361 0 0 1 0-.686l1.937-.645a2.89 2.89 0 0 0 1.828-1.828zM3.794 1.148a.217.217 0 0 1 .412 0l.387 1.162c.173.518.579.924 1.097 1.097l1.162.387a.217.217 0 0 1 0 .412l-1.162.387A1.73 1.73 0 0 0 4.593 5.69l-.387 1.162a.217.217 0 0 1-.412 0L3.407 5.69A1.73 1.73 0 0 0 2.31 4.593l-1.162-.387a.217.217 0 0 1 0-.412l1.162-.387A1.73 1.73 0 0 0 3.407 2.31zM10.863.099a.145.145 0 0 1 .274 0l.258.774c.115.346.386.617.732.732l.774.258a.145.145 0 0 1 0 .274l-.774.258a1.16 1.16 0 0 0-.732.732l-.258.774a.145.145 0 0 1-.274 0l-.258-.774a1.16 1.16 0 0 0-.732-.732L9.1 2.137a.145.145 0 0 1 0-.274l.774-.258c.346-.115.617-.386.732-.732z"/>
            </svg>
            New
          </span>
        ) : (
          !timeRemaining.isExpired && (
            <span className="countdown-timer">{formatTimeRemaining(timeRemaining)}</span>
          )
        )}
      </div>
    </div>
  );
};

export default EventCard;